import {
  ActionButton,
  formatDate,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  useOutputDescriptionProcessingStatesQuery,
  useSystemClassifierItemsBy,
  Form,
  OutputDescriptionProcessingStatesType,
  GetApiErrorMessage,
  FormAlerts,
  useMutationErrors,
  Detail,
  OutputDescriptionProcessingStateType,
  useDeleteOutputData,
  DeleteOutputDataType,
  DeleteOutputDataSchema,
  useAlertContext,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { FC, useCallback } from 'react';
import translations from './OutputDataActionModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ActiveOutputsTable } from './ActiveOutputsTable';
import { useFormContext, useWatch } from 'react-hook-form';
import { OutputDataActionParams } from '../OutputDataDirectory';

const { addTranslations, t, exists } = i18nNsFactory(
  'output-data-action-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export interface DeletionModalProps {
  params: OutputDataActionParams;
  onClose: () => void;
}

export const DeletionModal: FC<DeletionModalProps> = (props) => {
  const {
    params: { hasAccess, ...otherParams },
    onClose,
  } = props;
  const { data, isLoading, refetch } =
    useOutputDescriptionProcessingStatesQuery(otherParams);
  const { mutateAsync } = useDeleteOutputData();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  const handleDelete = useCallback(
    (data: DeleteOutputDataType) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({ data })
        .then(() => {
          onClose();
          setSuccess(t('alert.success.deleted'));
        })
        .catch((error) => {
          refetch().then(() => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
        });
    },
    [
      clearMutationErrors,
      mutateAsync,
      onClose,
      refetch,
      resetAlert,
      setMutationErrors,
      setSuccess,
    ],
  );

  const handleSubmit = useCallback(
    (data: DeleteOutputDataType) => {
      Modal.confirm({
        title: t('confirm.delete.title'),
        okText: t('confirm.yes'),
        cancelText: t('confirm.no'),
        content: t('confirm.delete.text'),
        onOk: () => handleDelete(data),
      });
    },
    [handleDelete],
  );

  if (!data) {
    return null;
  }

  return (
    <Form
      schema={DeleteOutputDataSchema}
      defaultValues={{ groupId: data.groupId }}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
    >
      <DeletionModalInner
        data={data}
        isLoading={isLoading}
        onClose={onClose}
        hasGroupAccess={hasAccess}
      />
    </Form>
  );
};

const DeletionModalInner: FC<{
  data: OutputDescriptionProcessingStatesType;
  isLoading?: boolean;
  onClose: () => void;
  hasGroupAccess: boolean;
}> = (props) => {
  const { data, isLoading, onClose, hasGroupAccess } = props;

  const { setValue } = useFormContext<DeleteOutputDataType>();
  const selectedActiveVersionId = useWatch<DeleteOutputDataType>({
    name: 'versionId',
  }) as number;
  const selectedOutputId = data.outputs.find(
    (output) => output.activeDataObjectVersionId === selectedActiveVersionId,
  )?.id;

  const systemClassifierItemsById = useSystemClassifierItemsBy(
    ['FREQUENCY', 'OUTPUT_TYPE'],
    'id',
  );

  const handleSelectedChange = useCallback(
    (selectedOutputId?: number) => {
      const activeVersionId = data.outputs.find(
        (output) => output.id === selectedOutputId,
      )?.activeDataObjectVersionId;
      if (!activeVersionId) return;

      setValue('versionId', activeVersionId);
    },
    [data.outputs, setValue],
  );

  if (!data) return null;

  return (
    <Modal
      onCancel={onClose}
      width="60rem"
      loading={isLoading}
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton
              type="formSubmit"
              label={t('button.delete')}
              disabled={!selectedOutputId}
            />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>
            {t('title', { count: data.outputs.length })}
          </TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="small">
          <Detail label={t('group')}>
            {`${data?.groupCode} - ${data.groupName}`}
          </Detail>
          <Detail label={t('valueDate')}>{formatDate(data.valueDate)}</Detail>
          <Detail label={t('frequency')}>
            {systemClassifierItemsById['FREQUENCY'].get(data.frequencyId)?.name}
          </Detail>
        </Flex>
        <Padding top>
          <ActiveOutputsTable
            mode="edit"
            data={data}
            hasGroupAccess={hasGroupAccess}
            selectionMode="single"
            isSelectable={(output) =>
              isSelectable(output, systemClassifierItemsById['OUTPUT_TYPE'])
            }
            selectedOutputId={selectedOutputId}
            onSelectedOutputIdChange={handleSelectedChange}
          />
        </Padding>
      </Padding>
    </Modal>
  );
};

const isSelectable = (
  output: OutputDescriptionProcessingStateType,
  outputTypes: Map<number, { code: string }>,
) =>
  outputTypes.get(output.outputTypeId)?.code !== 'INTERMEDIATE' &&
  output.activeDataObjectVersionId !== null;
